import React, { useRef } from 'react';
import { useDialogA11y } from '../hooks/useDialogA11y';
import './AppDialog.css';

type ActionVariant = 'primary' | 'secondary' | 'danger';

export type AppDialogAction = {
  label: string;
  onClick: () => void;
  variant?: ActionVariant;
  disabled?: boolean;
};

export interface AppDialogProps {
  isOpen: boolean;
  title: string;
  description?: React.ReactNode;
  onClose: () => void;
  primaryAction?: AppDialogAction;
  secondaryAction?: AppDialogAction;
  children?: React.ReactNode;
}

export const AppDialog: React.FC<AppDialogProps> = ({
  isOpen,
  title,
  description,
  onClose,
  primaryAction,
  secondaryAction,
  children
}) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  useDialogA11y({ isOpen, onClose, dialogRef });

  if (!isOpen) return null;

  const primaryVariant = primaryAction?.variant || 'primary';
  const secondaryVariant = secondaryAction?.variant || 'secondary';

  return (
    <div
      className="app-dialog__overlay"
      role="presentation"
      onClick={onClose}
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        className="app-dialog"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="app-dialog__header">
          <h3 className="app-dialog__title">{title}</h3>
          <button
            type="button"
            className="app-dialog__close"
            onClick={onClose}
            aria-label="Close dialog"
          >
            ×
          </button>
        </div>

        {(description || children) && (
          <div className="app-dialog__body">
            {description && <div className="app-dialog__description">{description}</div>}
            {children}
          </div>
        )}

        {(primaryAction || secondaryAction) && (
          <div className="app-dialog__actions">
            {secondaryAction && (
              <button
                type="button"
                className={`app-dialog__button app-dialog__button--${secondaryVariant}`}
                onClick={secondaryAction.onClick}
                disabled={secondaryAction.disabled}
              >
                {secondaryAction.label}
              </button>
            )}
            {primaryAction && (
              <button
                type="button"
                className={`app-dialog__button app-dialog__button--${primaryVariant}`}
                onClick={primaryAction.onClick}
                disabled={primaryAction.disabled}
              >
                {primaryAction.label}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

